import React, { useState } from 'react';
import { Survey } from '@questlabs/react-sdk';
import SafeIcon from '../common/SafeIcon';

const REVIEW_CANDIDATES = [
  { id: 1, name: 'Yaw Mensah', role: 'Operations Manager', experience: '8 Years', education: 'MBA, KNUST', avatar: 'https://i.pravatar.cc/150?img=11', tags: ['Logistics', 'Leadership'] },
  { id: 2, name: 'Ama Serwaa', role: 'Frontend Developer', experience: '5 Years', education: 'BSc Computer Science, UG', avatar: 'https://i.pravatar.cc/150?img=32', tags: ['React', 'UI/UX'] },
  { id: 3, name: 'Ekow Thompson', role: 'Sales Executive', experience: '12 Years', education: 'B.A. Marketing, GIMPA', avatar: 'https://i.pravatar.cc/150?img=12', tags: ['B2B', 'Negotiation'] },
];

const ProfileReviews = () => {
  const [selectedCandidate, setSelectedCandidate] = useState(REVIEW_CANDIDATES[0]);

  const styleConfig = {
    Form: { backgroundColor: '#FFFFFF', borderRadius: '8px', padding: '24px', boxShadow: 'none', border: '1px solid #f1f5f9' },
    Heading: { color: '#11469A', fontSize: '18px', fontWeight: '700' },
    Description: { color: '#64748b', fontSize: '14px', marginBottom: '20px' },
    Label: { color: '#475569', fontSize: '13px', fontWeight: '600', marginBottom: '8px' },
    Input: { border: '1px solid #e2e8f0', borderRadius: '6px', height: '44px' },
    TextArea: { border: '1px solid #e2e8f0', borderRadius: '6px', minHeight: '100px' },
    PrimaryButton: { backgroundColor: '#00AEEF', color: '#FFFFFF', borderRadius: '6px', fontWeight: '700', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em' },
    SingleChoice: { 
      style: { backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '12px' },
      selectedStyle: { backgroundColor: '#11469A', color: '#FFFFFF', border: '1px solid #11469A' },
      hoverBackground: '#f1f5f9' 
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Candidate Profile Reviews</h1>
          <p className="text-gray-500">Provide structured feedback and ratings for applicants.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4 space-y-4">
          <div className="card overflow-hidden">
            <div className="p-4 bg-gray-50 border-b border-gray-100 font-bold text-xs uppercase tracking-widest text-gray-400">
              Pending Reviews
            </div>
            <div className="divide-y divide-gray-100">
              {REVIEW_CANDIDATES.map((candidate) => (
                <button 
                  key={candidate.id} 
                  onClick={() => setSelectedCandidate(candidate)}
                  className={`w-full flex items-center gap-4 p-4 text-left transition-all ${selectedCandidate.id === candidate.id ? 'bg-blue-50/50 border-r-4 border-r-[#11469A]' : 'hover:bg-gray-50 border-r-4 border-r-transparent'}`}
                >
                  <img src={candidate.avatar} className="w-12 h-12 rounded-full border-2 border-white shadow-sm" alt="" />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-gray-800 truncate">{candidate.name}</h4>
                    <p className="text-xs text-brand-primary font-semibold truncate">{candidate.role}</p>
                  </div>
                  <SafeIcon name="ChevronRight" size={16} className={selectedCandidate.id === candidate.id ? "text-brand-primary" : "text-gray-300"} />
                </button>
              ))}
            </div>
          </div>
          <div className="card p-5 bg-[#11469A] text-white">
            <h4 className="font-bold mb-2">Review Guidelines</h4>
            <ul className="text-xs space-y-2 text-white/80">
              <li className="flex items-start gap-2"><SafeIcon name="Check" size={14} className="mt-0.5 text-[#00AEEF]" /> Evaluate based on technical merit.</li>
              <li className="flex items-start gap-2"><SafeIcon name="Check" size={14} className="mt-0.5 text-[#00AEEF]" /> Consider cultural alignment.</li>
              <li className="flex items-start gap-2"><SafeIcon name="Check" size={14} className="mt-0.5 text-[#00AEEF]" /> Provide constructive comments.</li>
            </ul>
          </div>
        </div>

        <div className="lg:col-span-8 space-y-6">
          <div className="card p-6 border-none shadow-sm bg-white">
            <div className="flex flex-col md:flex-row gap-6 items-start md:items-center mb-8 pb-8 border-b border-gray-100">
              <img src={selectedCandidate.avatar} className="w-20 h-20 rounded-2xl shadow-lg" alt="" />
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <h2 className="text-2xl font-bold text-gray-800">{selectedCandidate.name}</h2>
                  <span className="px-2 py-0.5 bg-green-50 text-green-600 rounded text-[10px] font-bold uppercase tracking-widest">Active Review</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div className="flex items-center gap-2 text-gray-500 font-medium font-medium">
                    <SafeIcon name="Briefcase" size={16} className="text-gray-400" /> {selectedCandidate.experience}
                  </div>
                  <div className="flex items-center gap-2 text-gray-500 font-medium">
                    <SafeIcon name="BookOpen" size={16} className="text-gray-400" /> {selectedCandidate.education}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                {selectedCandidate.tags.map((tag, i) => (
                  <span key={i} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-[10px] font-bold uppercase tracking-wider">{tag}</span>
                ))}
              </div>
            </div>
            <Survey styleConfig={styleConfig} questId="candidate-review-form" userId="admin-interviewer" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileReviews;